import { TeamReferences, type TeamReference } from "./TeamReferences";
import {
  TeamNoteExtras,
  TeamNoteNotifications,
  type NoteExtrasData,
} from "./TeamNoteExtras";
import { lazy, Suspense, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "../api/http";
import { useAuthStore } from "../store/auth";
const SharedBoard = lazy(() => import("./TeamBoard"));
type Kind = "notes" | "meetings" | "boards";
type Item = { id: string; title: string };
type Detail = Item &
  NoteExtrasData & {
    references?: TeamReference[];
    body?: string;
    agenda?: string;
    minutes?: string;
    decisions?: {
      id: string;
      title: string;
      body: string;
      reference?: TeamReference | null;
    }[];
    scene?: Parameters<typeof SharedBoard>[0]["scene"];
  };
export default function TeamView({
  initial,
}: { initial?: { kind: Kind; id: string } } = {}) {
  const user = useAuthStore((s) => s.user);
  const [kind, setKind] = useState<Kind>(initial?.kind ?? "notes"),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState<string | null>(initial?.id ?? null);
  const list = useQuery({
    queryKey: ["team", user?.id, kind, page],
    queryFn: async () =>
      (
        await http.get<{ items: Item[]; hasMore: boolean }>(
          "/v1/workspace/" + kind,
          { params: { page } },
        )
      ).data,
    gcTime: 0,
    refetchInterval: 15000,
  });
  const detail = useQuery({
    queryKey: ["team-detail", user?.id, kind, selected],
    queryFn: async () =>
      (await http.get<Detail>("/v1/workspace/" + kind + "/" + selected)).data,
    enabled: !!selected,
    gcTime: 0,
    refetchInterval: 10000,
  });
  const conference = useQuery({
    queryKey: ["team-conference", user?.id, selected],
    queryFn: async () =>
      (
        await http.get<{ state: string; url: string | null }>(
          "/v1/meeting-conferences/" + selected,
        )
      ).data,
    enabled: kind === "meetings" && !!selected,
    refetchInterval: 15000,
    gcTime: 0,
    retry: false,
  });
  return (
    <section className="space-y-4">
      <h1 className="text-xl font-bold text-slate-900">Espace Équipe</h1>
      {kind === "meetings" &&
        !conference.isError &&
        conference.data?.state === "READY" &&
        conference.data.url && (
          <a
            className="inline-block rounded-xl bg-indigo-600 px-4 py-2 text-white"
            href={conference.data.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Rejoindre Meet
          </a>
        )}
      <p className="text-slate-500">
        Contenus explicitement partagés avec vous, en consultation.
      </p>
      <div className="flex flex-wrap gap-3">
        {(["notes", "meetings", "boards"] as Kind[]).map((k) => (
          <button
            key={k}
            className={`rounded-xl px-4 py-2 ${kind === k ? "bg-indigo-600 text-white" : "bg-white text-slate-700 border"}`}
            onClick={() => {
              setKind(k);
              setPage(1);
              setSelected(null);
            }}
          >
            {{ notes: "Notes", meetings: "Réunions", boards: "Tableaux" }[k]}
          </button>
        ))}
      </div>
      <TeamNoteNotifications
        onOpen={(id) => {
          setKind("notes");
          setSelected(id);
        }}
      />
      {list.isError ? (
        <p role="alert">Impossible de vérifier les contenus partagés.</p>
      ) : (
        list.data?.items.map((i) => (
          <button
            className="block w-full rounded-xl border bg-white p-4 text-left"
            key={i.id}
            onClick={() => setSelected(i.id)}
          >
            {i.title}
          </button>
        ))
      )}
      {list.data?.items.length === 0 && <p>Aucun contenu partagé.</p>}
      <div className="flex gap-3">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>
          Précédents
        </button>
        <button
          disabled={!list.data?.hasMore}
          onClick={() => setPage(page + 1)}
        >
          Suivants
        </button>
      </div>
      {selected && (
        <article className="rounded-2xl border bg-white p-5 space-y-4">
          {detail.isError ? (
            <p role="alert">Contenu indisponible ou partage retiré.</p>
          ) : !detail.data || detail.isFetching ? (
            <p>Vérification de l’accès…</p>
          ) : (
            <>
              <h2 className="text-lg font-bold">{detail.data.title}</h2>
              {[detail.data.body, detail.data.agenda, detail.data.minutes]
                .filter(Boolean)
                .map((text, i) => (
                  <p className="whitespace-pre-wrap" key={i}>
                    {text}
                  </p>
                ))}
              {kind === "notes" && (
                <TeamNoteExtras id={selected} data={detail.data} />
              )}
              <TeamReferences
                references={
                  detail.data.references ?? detail.data.resolvedReferences ?? []
                }
                onOpen={(next, id) => {
                  setKind(next);
                  setSelected(id);
                  setPage(1);
                }}
              />
              {detail.data.scene?.cards?.length ? (
                <TeamReferences
                  references={detail.data.scene.cards.map((c) => c.reference)}
                  onOpen={(next, id) => {
                    setKind(next);
                    setSelected(id);
                    setPage(1);
                  }}
                />
              ) : null}
              {detail.data.decisions?.map((d) => (
                <section key={d.id}>
                  <h3 className="font-semibold">{d.title}</h3>
                  <TeamReferences
                    references={d.reference ? [d.reference] : []}
                    onOpen={(next, id) => {
                      setKind(next);
                      setSelected(id);
                      setPage(1);
                    }}
                  />
                  <p className="whitespace-pre-wrap">{d.body}</p>
                </section>
              ))}
              {detail.data.scene && (
                <Suspense fallback={<p>Chargement du tableau…</p>}>
                  <SharedBoard scene={detail.data.scene} boardId={selected} />
                </Suspense>
              )}
            </>
          )}
        </article>
      )}
    </section>
  );
}
